import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DumbbellIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShieldCheckIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/cf361016-8c31-4801-80df-8c027a50b1ee/1792460760131_pilates-klar-mockup-a4801706.webp';
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];
export const stats = [{
  value: '240',
  label: 'Sidor i huvudvolymerna'
}, {
  value: '6',
  label: 'Digitala pilatesvolymer'
}, {
  value: '12',
  label: 'Klasser minut för minut'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}, {
  value: '1',
  label: 'Komplett undervisningssystem'
}];
export const differenceStats = [{
  value: 'Matta + reformer',
  label: 'Material för grupp, privat och små redskap'
}, {
  value: '6 digitala volymer',
  label: 'Från årsplanering och klasser till modifieringar och uppföljning'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda på surfplatta eller skriva ut i studion'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällarna försvinner',
  text: 'Timmar framför datorn för att planera nästa veckas pass – och du är fortfarande inte klar.'
}, {
  title: 'Samma klass om och om igen',
  text: 'Du kör samma övningar i samma ordning eftersom det inte finns tid att bygga nya sekvenser.'
}, {
  title: 'Osäker med blandade nivåer',
  text: 'Nybörjare, vana deltagare och någon med ont i ryggen – i samma klass. Vilken variant ska du visa?'
}, {
  title: 'Gravida, ryggbesvär och seniorer',
  text: 'Du vill vara säker på vad du kan erbjuda och vad du bör undvika – utan att leta i tio olika källor.'
}, {
  title: 'Svårt att visa framsteg',
  text: 'Deltagarna märker inte hur mycket de har utvecklats, och det är lätt att de slutar komma.'
}, {
  title: 'Stress inför nästa pass',
  text: 'När tiden inte räcker till blir det improvisation i sista minuten framför gruppen.'
}];
export const afterItems = ['Ett organiserat system för hela året', '12 klasser planerade minut för minut', 'Mattövningar i tre nivåer med cues', 'Reformer- och redskapsklasser klara att köra', 'Modifieringar för graviditet, rygg och seniorer', 'Tematiska sekvenser med musikstruktur', 'Utskrivbara klassblad och checklistor', 'Bedömning och framstegskort för deltagarna', 'Planeringsstöd för varje vecka'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Helgerna går åt till planering',
  text: 'Lägger du kvällar och helger på att skriva nästa veckas pass?'
}, {
  icon: FolderSearchIcon,
  title: 'Du letar efter nya övningar',
  text: 'Scrollar du efter idéer som passar din grupp och nivå – och hittar inget färdigt?'
}, {
  icon: LightbulbIcon,
  title: 'Du vill ha tydligare cues',
  text: 'Vill du ha färdiga instruktioner som får deltagarna att förstå rörelsen direkt?'
}, {
  icon: ShieldCheckIcon,
  title: 'Du vill känna dig trygg',
  text: 'Vill du veta exakt hur du modifierar för gravida, ryggbesvär och äldre deltagare?'
}, {
  icon: ShuffleIcon,
  title: 'Deltagarna behöver variation',
  text: 'Vill du ge dina klasser nya teman, redskap och progressioner utan att uppfinna allt själv?'
}, {
  icon: LayoutGridIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha allt samlat så att du hittar rätt klass på några sekunder?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Alla nivåer',
  title: 'Årsplanering + 12 klasser minut för minut',
  tagline: 'Ett helt år planerat – och 12 kompletta klasser att köra direkt.',
  intro: 'Den första volymen ger dig en årsplan i fyra block och 12 färdiga klasser på 55 minuter, planerade minut för minut.',
  items: ['Årsplan i 4 block à 12 veckor', 'Veckoteman och klassmål', '12 klasser à 55 minuter', 'Uppvärmning, huvuddel och nedvarvning med tider', 'Cues och andning för varje övning', 'Regressioner och progressioner', 'Utskrivbara klassblad'],
  outro: 'Varje klass har mål, tidsschema, övningar, cues, varianter och avslutning.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Matta',
  title: 'Mattpilates i tre nivåer',
  tagline: 'Klassisk mattrepertoar för nybörjare, mellannivå och avancerade.',
  intro: 'Den andra volymen samlar mattövningarna i tre nivåer med instruktion steg för steg och färdiga mattklasser.',
  items: ['Grundprinciper: andning, neutral position, center', 'Övningar på nivå 1, 2 och 3', 'Instruktion steg för steg', 'Vanliga fel och hur du rättar dem', 'Regressioner och progressioner', '6 färdiga mattklasser'],
  outro: 'Du ser direkt vilken variant som passar varje deltagare.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Reformer + redskap',
  title: 'Reformer och små redskap',
  tagline: 'Fjäderinställningar, övningar och klasser med reformer, boll, ring och band.',
  intro: 'Den tredje volymen ger dig reformerövningar med fjäderinställningar samt klasser med små redskap.',
  items: ['Reformer: fjädrar, fotstång och säkerhet', 'Footwork, armar, ben och core på reformer', 'Liten boll och pilatesring', 'Gummiband och foam roller', 'Klasser för grupp och privat', 'Utskrivbara klassblad'],
  outro: 'Färdiga upplägg både för reformerstudion och för mattklasser med redskap.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 04',
  level: 'Säkerhet',
  title: 'Modifieringar och säkerhet',
  tagline: 'Anpassa tryggt för graviditet, rygg och seniorer.',
  intro: 'Den fjärde volymen hjälper dig att anpassa övningar och klasser för deltagare med särskilda behov.',
  items: ['Hälsodeklaration och screening före start', 'Graviditet och efter förlossning', 'Ländrygg, nacke och axlar', 'Seniorer och balans', 'Trafikljussystemet: kör, anpassa, undvik', 'Färdiga anpassade klasser'],
  outro: 'Tydliga riktlinjer som gör dig trygg framför varje grupp.',
  note: 'Materialet ersätter inte medicinsk bedömning. Vid besvär hänvisar du deltagaren till vården.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Variation',
  title: 'Tematiska sekvenser och musik',
  tagline: 'Nya teman varje vecka – med musikstruktur och tempo.',
  intro: 'Den femte volymen innehåller tematiska sekvenser som ger variation utan att du behöver bygga nya klasser själv.',
  items: ['Teman: core, hållning, rörlighet, balans', 'Pilates för löpare och för kontorsryggen', 'Snabba 30-minutersklasser', 'Musik: tempo (BPM) och spellistestruktur', 'Övergångar som håller flytet', 'Säsongsteman för hela året'],
  outro: 'Byt tema, behåll strukturen – deltagarna upplever något nytt varje gång.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 06',
  level: 'Uppföljning',
  title: 'Bedömning och uppföljning av deltagare',
  tagline: 'Se, dokumentera och visa deltagarnas framsteg.',
  intro: 'Den sjätte volymen ger dig verktyg för att följa varje deltagare från första klassen.',
  items: ['Intagsformulär och startpunkt', 'Enkel rörelsescreening', 'Framstegsskala i fyra steg', 'Klassanteckningar på 60 sekunder', 'Uppföljning efter 30 och 90 dagar', '100 återkopplingsfraser'],
  outro: 'Deltagarna ser sin utveckling – och fortsätter komma tillbaka.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitala volymer för pilatesinstruktörer',
  text: 'Ett organiserat system med färdiga klasser, övningar, cues och anpassningar för matta, reformer och små redskap. Öppna volymen, välj klass och undervisa. Mindre planering. Mer tid i studion.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Färdiga klasser',
  text: 'Slipp börja från ett tomt dokument. Klasser planerade minut för minut med övningar, tider och cues.'
}, {
  icon: FileTextIcon,
  title: 'Utskrivbara klassblad',
  text: 'Ta med klassen på ett papper eller på surfplattan – allt du behöver framför gruppen.'
}, {
  icon: DumbbellIcon,
  title: 'Matta, reformer och redskap',
  text: 'Övningar och klasser för matta, reformer, liten boll, ring, band och foam roller.'
}, {
  icon: ShieldCheckIcon,
  title: 'Modifieringar och säkerhet',
  text: 'Tydliga varianter för gravida, ryggbesvär, seniorer och blandade nivåer.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömning och uppföljning',
  text: 'Intagsformulär, framstegskort och färdiga fraser för att visa deltagarnas utveckling.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplanering',
  text: 'Ett helt år i fyra block med veckoteman, så att varje klass bygger vidare på den förra.'
}];
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/cf361016-8c31-4801-80df-8c027a50b1ee/1792460764235_pilates-klar-v1-sida-12-klassblad-b070be3c.webp', label: 'Klassblad · Sida 12', title: 'Klass 1 – klassblad att skriva ut', text: 'Alla övningar med figurer, repetitioner, nivå 1–3 och tider – på ett papper eller surfplattan.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/cf361016-8c31-4801-80df-8c027a50b1ee/1792460768323_pilates-klar-v1-sida-11-klass1-4e56a0a7.webp', label: 'Färdig klass · Sida 11', title: 'Klass 1: Andning och neutral position', text: '55 minuter planerade minut för minut med incheckning, uppvärmning, huvuddel, utmaning, nedvarvning och exakta cues.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/cf361016-8c31-4801-80df-8c027a50b1ee/1792460772255_pilates-klar-v1-sida-4-principer-ff65c252.webp', label: 'Visuell genomgång · Sida 4', title: 'Pilates 6 principer + andning', text: 'Center, kontroll, precision, andning och flyt förklarade visuellt – med en cue för varje princip.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/cf361016-8c31-4801-80df-8c027a50b1ee/1792460776297_pilates-klar-v1-sida-5-arsplan-3e59e81b.webp', label: 'Årsplanering · Sida 5', title: 'Årsplan block 1: Grunden', text: 'Vecka för vecka med tema, mål och nyckelövning – så att varje klass bygger vidare på den förra.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/cf361016-8c31-4801-80df-8c027a50b1ee/1792460780313_pilates-klar-v1-sida-38-snabbguide-c85781c7.webp', label: 'Nivåer · Sida 38', title: 'När ska en deltagare gå upp eller ner i nivå?', text: 'Fem tydliga tecken åt varje håll – så att du ger rätt variant till varje deltagare.' }
];
export const pageTypes = ['Färdiga klasser', 'Utskrivbara klassblad', 'Övningar med cues', 'Modifieringar och uppföljning'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Klassplaneraren på 5 minuter',
  text: 'En snabb metod för att välja och bygga rätt klass för gruppen framför dig.',
  items: ['Väljarmatris: deltagare, mål, redskap, energi', 'Mallar för 30, 45 och 55 minuter', 'Nödprotokollet när du inte hunnit planera', 'Utskrivbar veckoplanering'],
  benefit: 'Aldrig mer ett tomt papper före passet.'
}, {
  title: 'Manus för nöjda och lojala deltagare',
  text: 'Färdiga formuleringar före, under och efter klassen.',
  items: ['Välkomstmanus för nya deltagare', 'Avslutning som visar framsteg', 'Meddelanden när någon uteblir', 'Återaktivering efter 30 dagar'],
  benefit: 'Fler deltagare som stannar kvar termin efter termin.'
}, {
  title: 'Kursbyggaren',
  text: 'Gör om enstaka klasser till kurser och paket med tydligt resultat.',
  items: ['Namnformel för kurser', 'Startkurs på 4 veckor', 'Program på 12 veckor', 'Prissättning och erbjudandemall'],
  benefit: 'Sälj resultat och kurser – inte bara enstaka klasser.'
}, {
  title: 'Checklista för studioupplevelsen',
  text: 'Små detaljer som gör att din klass känns premium.',
  items: ['Före, under och efter klassen', 'Ljus, musik och ordning i salen', 'Redskap och hygien', 'Veckovis studiokontroll'],
  benefit: 'En upplevelse som deltagarna vill komma tillbaka till.'
}, {
  title: 'Innehålls- och rekommendationskit',
  text: 'Idéer och mallar för att fylla dina klasser via sociala medier och rekommendationer.',
  items: ['30 dagars inläggsidéer', 'Formel för bildtexter', 'Rekommendationssystem', 'Innehållskalender att skriva ut'],
  benefit: 'Fler bokningar utan att du behöver vara säljare.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna PILATES KLAR™',
  text: 'Ladda ner paketet och öppna volymen som passar din klass.'
}, {
  title: 'Välj klass och nivå',
  text: 'Hitta rätt tema, redskap och nivå för gruppen du har framför dig.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Läs igenom tidsschemat, övningarna och cuesen.'
}, {
  title: 'Gå in och undervisa',
  text: 'Skriv ut klassbladet eller använd surfplattan – och gå in i studion med en tydlig plan.'
}];
export const audienceItems = ['Undervisar pilates på studio, gym eller friskvård', 'Är frilansande instruktör eller driver egen studio', 'Har nyligen utbildat dig och vill ha färdiga klasser', 'Undervisar matta, reformer eller båda', 'Vill känna dig trygg med gravida, ryggbesvär och seniorer', 'Vill ge deltagarna mer variation', 'Vill spara tid på veckoplaneringen', 'Vill känna dig bättre förberedd inför varje klass'];
export const timeValueTasks = ['Leta efter övningar och sekvenser', 'Skriva klassupplägg', 'Ta fram modifieringar', 'Välja musik och teman', 'Följa upp deltagarnas utveckling'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 240 sidor',
  detail: 'Årsplanering, 12 klasser, matta i tre nivåer, reformer och redskap, modifieringar, teman och uppföljning'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Klassplanerare, deltagarmanus, kursbyggare, studiochecklista och innehållskit'
}, {
  label: 'För pilatesinstruktörer',
  detail: 'Färdiga klasser, cues, modifieringar och klassblad'
}];
export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara klassblad', 'Ingen fysisk produkt skickas'];
export const PRICE = '299 kr';

// Only add authentic reviews of PILATES KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'PILATES KLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de klassblad du behöver.'
}, {
  q: 'Vem passar PILATES KLAR™ för?',
  a: 'För pilatesinstruktörer som undervisar i studio, på gym, inom friskvård eller som frilansare – både nyutbildade och erfarna instruktörer.'
}, {
  q: 'Behöver jag en reformer?',
  a: 'Nej. Volym 1, 2, 4, 5 och 6 fungerar med matta. Volym 3 innehåller både reformerövningar och klasser med små redskap som boll, ring, band och foam roller.'
}, {
  q: 'Kan jag verkligen förbereda en klass på 5 minuter?',
  a: 'PILATES KLAR™ är utformat för att göra förberedelserna snabbare. Du öppnar en volym, väljer en färdig klass och läser igenom tidsschemat och cuesen på ungefär fem minuter. Tiden kan variera beroende på grupp och behov av anpassning.'
}, {
  q: 'Fungerar det för blandade nivåer?',
  a: 'Ja. Övningarna har regressioner och progressioner i tre nivåer, så att du kan ge varje deltagare rätt variant i samma klass.'
}, {
  q: 'Ingår modifieringar för gravida och ryggbesvär?',
  a: 'Ja. Volym 4 innehåller riktlinjer och anpassade klasser för graviditet, efter förlossning, ländrygg, nacke och axlar samt seniorer. Materialet ersätter inte medicinsk bedömning.'
}, {
  q: 'Finns det färdiga klassblad?',
  a: 'Ja. Klasserna finns som utskrivbara klassblad med tidsschema, övningar och cues.'
}, {
  q: 'Måste jag skriva ut allt?',
  a: 'Nej. Du väljer själv vilka sidor du skriver ut, och du kan använda materialet digitalt på surfplattan.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Kan jag spara filerna?',
  a: 'Ja. Du kan ladda ner och spara PDF-filerna för fortsatt användning enligt produktens användarvillkor.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på dator, surfplatta och mobil.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. PILATES KLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom garantiperioden enligt villkoren.'
}];
export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
